/**
 * Shared chart configuration for analytics components
 * These are plain objects/functions, not Vue composables
 */
import { formatCompact, formatPubkey } from './useFormatters';

// Standard color palette - 25 distinct colors for program charts
export const colors = {
    // Extended series with 25 colors - maintains Compass aesthetic with sufficient contrast
    series: [
        '#8b5cf6',  // compass purple
        '#ec4899',  // compass pink
        '#10f7cf',  // compass cyan
        '#f59e0b',  // amber
        '#3b82f6',  // blue
        '#22c55e',  // green
        '#ef4444',  // red
        '#a855f7',  // violet
        '#14b8a6',  // teal
        '#fb923c',  // orange
        '#6366f1',  // indigo
        '#06b6d4',  // cyan-500
        '#84cc16',  // lime
        '#f43f5e',  // rose
        '#0ea5e9',  // sky
        '#d946ef',  // fuchsia
        '#eab308',  // yellow
        '#64748b',  // slate
        '#10b981',  // emerald
        '#7c3aed',  // violet-600
        '#2dd4bf',  // teal-400
        '#facc15',  // yellow-400
        '#c084fc',  // purple-400
        '#f472b6',  // pink-400
        '#38bdf8',  // sky-400
    ],
    primary: '#8b5cf6',    // Compass purple
    accent: '#10f7cf',     // Compass cyan/teal
    secondary: '#ec4899',  // Compass pink
    positive: '#22c55e',
    negative: '#ef4444',
    neutral: '#6b7280',
    feeBreakdown: ['#6366f1', '#8b5cf6', '#f97316'], // Base, Priority, Tips
    jito: '#f97316',
    regular: '#8b5cf6',
    completed: '#22c55e',
    reverted: '#ef4444',
    others: '#6b7280'      // Muted gray for "Others" category in limited charts
};

/**
 * Get theme-aware colors based on current mode
 * Call this when creating charts to get appropriate text/grid colors
 */
export function getThemeAwareColors() {
    const isDark = document.body.classList.contains('dark-mode');
    return {
        text: isDark ? '#e5e7eb' : '#374151',
        textMuted: isDark ? '#9ca3af' : '#6b7280',
        grid: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
        border: isDark ? 'rgba(139,92,246,0.2)' : 'rgba(139,92,246,0.1)'
    };
}

// System program addresses to filter
export const systemPrograms = [
    'TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA',  // Token Program
    'ATokenGPvbdGVxr1b2hvZbsiqW5xWH25efTNsLJA8knL', // Associated Token Account
    'TokenzQdBNbLqP5VEhdkAS6EPFLC1PHnBqCXEpPxuEb',  // Token-2022
    '11111111111111111111111111111111'               // System Program
];

/**
 * Get base chart options with theme-aware colors
 * @param {boolean} useDynamicColors - If true, colors update on theme change (requires chart refresh)
 */
export function getBaseOptions(useDynamicColors = false) {
    const theme = useDynamicColors ? getThemeAwareColors() : { textMuted: '#9ca3af', grid: 'rgba(255,255,255,0.05)' };

    return {
        responsive: true,
        maintainAspectRatio: false,
        // Make tooltips easier to trigger on line charts
        interaction: {
            mode: 'index',
            intersect: false
        },
        hover: {
            mode: 'index',
            intersect: false
        },
        plugins: {
            legend: {
                labels: {
                    color: theme.textMuted,
                    font: { family: "'JetBrains Mono', monospace" }
                }
            },
            tooltip: {
                backgroundColor: 'rgba(17, 17, 17, 0.95)',
                titleColor: '#fff',
                bodyColor: '#e5e7eb',
                borderColor: 'rgba(139, 92, 246, 0.3)',
                borderWidth: 1,
                padding: 12,
                displayColors: true,
                boxPadding: 4
            }
        },
        scales: {
            x: {
                ticks: { color: theme.textMuted },
                grid: { color: theme.grid }
            },
            y: {
                ticks: { color: theme.textMuted },
                grid: { color: theme.grid },
                min: 0  // Prevent zooming to negative values
            }
        },
        // Remove borders from bars by default - CRITICAL FIX for border issue
        elements: {
            bar: {
                borderWidth: 0,
                borderRadius: 2
            },
            point: {
                radius: 3,
                hoverRadius: 6,
                hitRadius: 10  // Larger hit area for line chart points
            }
        }
    };
}

// Legacy export for backwards compatibility
export const baseOptions = getBaseOptions(false);

/**
 * Get bar chart options (horizontal or vertical)
 * Zoom is DISABLED by default, pan is enabled for exploration
 */
export function getBarChartOptions(options = {}) {
    const {
        horizontal = false,
        showLegend = false,
        formatCallback = formatCompact,
        title = null,
        enablePan = true,
        useDynamicColors = false
    } = options;

    const base = getBaseOptions(useDynamicColors);
    const theme = useDynamicColors ? getThemeAwareColors() : { textMuted: '#9ca3af' };

    return {
        ...base,
        indexAxis: horizontal ? 'y' : 'x',
        plugins: {
            ...base.plugins,
            legend: { display: showLegend },
            // Disable zoom, optionally enable pan
            zoom: {
                pan: {
                    enabled: enablePan,
                    mode: 'x'
                },
                zoom: {
                    wheel: { enabled: false },
                    pinch: { enabled: false },
                    mode: 'x'
                }
            }
        },
        scales: {
            ...base.scales,
            [horizontal ? 'x' : 'y']: {
                ...base.scales.y,
                ticks: {
                    ...base.scales.y.ticks,
                    callback: formatCallback
                },
                min: 0,  // Prevent negative values
                ...(title && { title: { display: true, text: title, color: theme.textMuted } })
            }
        }
    };
}

/**
 * Get doughnut/pie chart options
 * No zoom support for circular charts
 */
export function getDoughnutChartOptions(options = {}) {
    const { showLegend = true, legendPosition = 'right', useDynamicColors = false } = options;
    const theme = useDynamicColors ? getThemeAwareColors() : { textMuted: '#9ca3af' };

    return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: showLegend,
                position: legendPosition,
                labels: {
                    color: theme.textMuted,
                    font: { family: "'JetBrains Mono', monospace" }
                }
            }
        }
    };
}

/**
 * Get line chart options with zoom support
 * Line charts support zoom for time-series exploration
 */
export function getLineChartOptions(options = {}) {
    const {
        formatCallback = formatCompact,
        title = null,
        fill = false,
        onZoom = null,
        enableZoom = true,
        useDynamicColors = false
    } = options;

    const base = getBaseOptions(useDynamicColors);
    const theme = useDynamicColors ? getThemeAwareColors() : { textMuted: '#9ca3af' };

    return {
        ...base,
        scales: {
            ...base.scales,
            y: {
                ...base.scales.y,
                ticks: {
                    ...base.scales.y.ticks,
                    callback: formatCallback
                },
                min: 0,  // Prevent negative values on zoom
                ...(title && { title: { display: true, text: title, color: theme.textMuted } })
            }
        },
        elements: {
            line: { tension: 0.3, fill }
        },
        plugins: {
            ...base.plugins,
            zoom: {
                pan: {
                    enabled: true,
                    mode: 'x'
                },
                zoom: {
                    wheel: { enabled: enableZoom },
                    pinch: { enabled: enableZoom },
                    mode: 'x',
                    onZoomComplete: onZoom
                }
            }
        }
    };
}

/**
 * Get stacked bar chart options
 * Zoom DISABLED by default for bar charts, pan enabled for exploration
 */
export function getStackedBarChartOptions(options = {}) {
    const {
        formatCallback = formatCompact,
        onZoom = null,
        enableZoom = false,  // Disabled by default for stacked bars
        enablePan = true,
        useDynamicColors = false
    } = options;

    const base = getBaseOptions(useDynamicColors);

    return {
        ...base,
        scales: {
            x: {
                ...base.scales.x,
                stacked: true
            },
            y: {
                ...base.scales.y,
                stacked: true,
                min: 0,  // Prevent negative values
                ticks: {
                    ...base.scales.y.ticks,
                    callback: formatCallback
                }
            }
        },
        plugins: {
            ...base.plugins,
            zoom: {
                pan: {
                    enabled: enablePan,
                    mode: 'x'
                },
                zoom: {
                    wheel: { enabled: enableZoom },
                    pinch: { enabled: enableZoom },
                    mode: 'x',
                    onZoomComplete: onZoom
                }
            }
        }
    };
}

/**
 * Vue composable wrapper for backwards compatibility
 */
export function useChartConfig() {
    return {
        colors,
        systemPrograms,
        baseOptions,
        getBaseOptions,
        getThemeAwareColors,
        getBarChartOptions,
        getDoughnutChartOptions,
        getLineChartOptions,
        getStackedBarChartOptions
    };
}

export default useChartConfig;
